// ============================================================================
// PATIENT BUDGETS BLOCK — Histórico de presupuestos del paciente
// v1.2: indicador visual cuando pendingAmount < 0 (saldo a favor del paciente)
// ============================================================================
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ExternalLink, Bell, RotateCcw } from 'lucide-react';
import { budgetsApi } from '../../api/budgets';
import type { BudgetListItem } from '../../types/budget';
import { BUDGET_STATUS_COLORS, BUDGET_STATUS_LABELS } from '../../types/budget';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { formatDate } from '../../utils/format';

type BudgetFilter = 'all' | 'active' | 'paid' | 'rejected';

interface PatientBudgetsBlockProps {
  patientId: string;
  userId?: string;
}

function formatEuro(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

// Detecta si hay un saldo a favor del paciente (pagado > total)
function hasOverpayment(b: BudgetListItem): boolean {
  return (b.pendingAmount ?? 0) < 0;
}

/**
 * Barra de progreso de pagado/total.
 * Si hay saldo a favor del paciente (pagado > total), muestra trazo violeta.
 */
function PaymentProgressBar({
  paid,
  total,
  isOverpaid,
}: {
  paid: number;
  total: number;
  isOverpaid: boolean;
}) {
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  let barColor = 'bg-gray-300';
  if (isOverpaid) {
    barColor = 'bg-violet-500';
  } else if (pct === 100) {
    barColor = 'bg-green-500';
  } else if (pct > 0) {
    barColor = 'bg-blue-500';
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
        <span>{formatEuro(paid)}</span>
        <span className="text-gray-400">/ {formatEuro(total)}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      {isOverpaid && (
        <p className="text-xs text-violet-700 mt-1 flex items-center gap-1">
          <RotateCcw className="h-3 w-3" />
          {formatEuro(Math.abs((total || 0) - (paid || 0)))} a favor del paciente
        </p>
      )}
    </div>
  );
}

export function PatientBudgetsBlock({ patientId, userId }: PatientBudgetsBlockProps) {
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState<BudgetListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<BudgetFilter>('all');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // IMPORTANTE: no enviar includeRectifications cuando es false.
      // Con enableImplicitConversion, el string 'false' se invierte a true.
      const result = await budgetsApi.getAll({
        patientId,
        page: 1,
        limit: 100,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });
      setBudgets(result.data);
    } catch (err: any) {
      setError(err?.message || 'Error al cargar presupuestos');
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const filtered = budgets.filter((b) => {
    if (filter === 'all') return true;
    if (filter === 'rejected') return b.status === 'RECHAZADO';
    if (filter === 'paid') {
      return b.status === 'ACEPTADO' && (b.pendingAmount === 0 || b.pendingAmount === null);
    }
    if (filter === 'active') {
      const isActive = b.status === 'ENVIADO' || b.status === 'BORRADOR';
      const acceptedWithDebt = b.status === 'ACEPTADO' && (b.pendingAmount ?? 0) > 0;
      return isActive || acceptedWithDebt;
    }
    return true;
  });

  const counts = {
    all: budgets.length,
    active: budgets.filter((b) => b.status === 'ENVIADO' || b.status === 'BORRADOR' || (b.status === 'ACEPTADO' && (b.pendingAmount ?? 0) > 0)).length,
    paid: budgets.filter((b) => b.status === 'ACEPTADO' && (b.pendingAmount === 0 || b.pendingAmount === null)).length,
    rejected: budgets.filter((b) => b.status === 'RECHAZADO').length,
  };

  const filterButton = (label: string, key: BudgetFilter, count: number) => (
    <button
      type="button"
      onClick={() => setFilter(key)}
      className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
        filter === key
          ? 'bg-[#008DD0] text-white border-[#008DD0]'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
      }`}
    >
      {label} <span className="opacity-70">({count})</span>
    </button>
  );

  return (
    <CollapsibleSection
      id="budgets"
      title="Presupuestos"
      icon={<FileText className="h-5 w-5 text-gray-400" />}
      badge={
        <span className="text-sm font-normal text-gray-500">
          {budgets.length} {budgets.length === 1 ? 'presupuesto' : 'presupuestos'}
        </span>
      }
      defaultOpen={true}
      userId={userId}
    >
      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-4">
        {filterButton('Todos', 'all', counts.all)}
        {filterButton('Activos', 'active', counts.active)}
        {filterButton('Pagados', 'paid', counts.paid)}
        {filterButton('Rechazados', 'rejected', counts.rejected)}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm mb-3">
          {error}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-400">Cargando...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500 italic py-4 text-center">
          {budgets.length === 0
            ? 'Este paciente aún no tiene presupuestos.'
            : 'No hay presupuestos que coincidan con el filtro seleccionado.'}
        </p>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
                <th className="px-3 py-2 font-medium">Nº</th>
                <th className="px-3 py-2 font-medium">Fecha</th>
                <th className="px-3 py-2 font-medium">Tratamiento</th>
                <th className="px-3 py-2 font-medium">Estado</th>
                <th className="px-3 py-2 font-medium min-w-[200px]">Pagado / Total</th>
                <th className="px-3 py-2 font-medium text-center">Extras</th>
                <th className="px-3 py-2" aria-label="Acciones"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((b) => {
                const statusColors = BUDGET_STATUS_COLORS[b.status] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
                const statusLabel = BUDGET_STATUS_LABELS[b.status] || b.status;
                const overpaid = hasOverpayment(b);
                return (
                  <tr
                    key={b.id}
                    onClick={() => navigate(`/presupuestos/${b.id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-3 py-2.5 font-mono text-xs text-gray-900 whitespace-nowrap">
                      {b.budgetNumber}
                      {b.version > 1 && (
                        <span className="ml-1 text-xs text-amber-600 font-semibold">v{b.version}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                      {formatDate(b.createdAt)}
                    </td>
                    <td className="px-3 py-2.5 text-gray-900">
                      {b.treatmentName || <span className="text-gray-400 italic">Sin tratamiento</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusColors.bg} ${statusColors.text}`}>
                          {statusLabel}
                        </span>
                        {overpaid && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800 border border-violet-200"
                            title="Pagado en exceso: hay saldo a favor del paciente"
                          >
                            <RotateCcw className="h-3 w-3" />
                            A devolver
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <PaymentProgressBar
                        paid={b.totalPaid || 0}
                        total={b.total || 0}
                        isOverpaid={overpaid}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {b.hasReminders && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium"
                          title="Tiene recordatorios"
                        >
                          <Bell className="h-3 w-3" /> Rec.
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <ExternalLink className="h-4 w-4 text-gray-400 inline" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleSection>
  );
}

export default PatientBudgetsBlock;
